import { compact, debounce, isNil } from 'lodash';
import queryString from 'query-string';
import React from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { withRouter } from 'react-router-dom';
import styled, { css, withTheme } from 'styled-components';
import type { Fund, Lot, Portfolio, PortfolioNodeDesignationEnum } from 'venn-api';
import type { Theme } from 'venn-ui-kit';
import { CellLoader, ColorUtils, GetColor, Tooltip, ZIndex } from 'venn-ui-kit';
import {
  analyticsService,
  assertNotNil,
  Dates,
  flattenNode,
  formatAllocation,
  FS,
  getHistoricalPortfolioNodeAllocationType,
  getMissingReturnsMessage,
  isOptionNode,
  Routes,
  useHasFF,
} from 'venn-utils';
import { useShouldUseNetAssetsInsteadOfAllocation } from '../hooks/leverageAndShort';
import PrintContainerDimensions from '../print/PrintContainerDimensions';
import { ActionsButton } from './actions/common';
import { AllocationDifference, AllocationValue } from './AllocationDifferenceTooltip';
import ItemName, { GhostFundNameClass, OutOfSubtreeNameClass } from './AllocationItemName';
import { AllocationPanelContext } from './AllocationPanelContext';
import AllocationStrategyActions from './AllocationStrategyActions';
import Handle from './drag-and-drop/Handle';
import { FundActions } from './FundActions';
import {
  type DeprecatedIncorrectlyDefinedItemPercentageProps,
  GhostFundAllocationInputClass,
  GhostFundAllocationValueClass,
  IncreasedSpecificityGhostFundWrapperClass,
  ItemAllocationPrimitives,
  ItemAllocationType,
} from './ItemAllocation';
import { Constants } from './Layout';
import type { AllocationPanelPortfolio } from './types';
import { OPTION_ICON_CLASS } from '../common/OptionIcon';
import { CASH_ICON_NEGATIVE_CLASS, CASH_ICON_POSITIVE_CLASS } from '../common/CashIcon';
import { hasNodeIncompleteTaxInfo } from './AllocationUtils';

const eps = 0.0000000001;

interface AllocationPanelHandleProps {
  allowActions: boolean;
  isRoot: boolean;
  isSelected: boolean;
  allocationPanelType: string;
  onDrag?: (event: React.MouseEvent<HTMLElement>) => void;
}

function AllocationPanelHandle({
  allowActions,
  isRoot,
  isSelected,
  allocationPanelType,
  onDrag,
}: AllocationPanelHandleProps) {
  const hasPortfolioAllocatorStrategyEnforcement = useHasFF('rcm_portfolio_strategy_enforcement_ff');

  if (hasPortfolioAllocatorStrategyEnforcement) {
    return null;
  }

  return (
    <Handle
      className="drag-and-drop-handle"
      showIcon={allowActions && !isRoot && !isSelected && allocationPanelType !== 'historical read-only'}
      onMouseDown={allowActions && !isRoot && !!onDrag ? onDrag : undefined}
      height={30}
    />
  );
}

export interface AllocationPanelRowProps extends DeprecatedIncorrectlyDefinedItemPercentageProps, RouteComponentProps {
  strategy?: AllocationPanelPortfolio;
  compareStrategy?: Portfolio;
  originalNode?: Portfolio;
  totalWidth: number;
  isRoot: boolean;
  isGhost: boolean;
  isStrategy: boolean;
  isSelected: boolean;
  isInSelectedSubtree: boolean;
  hideCompareValue: boolean;
  compareLoading: boolean;
  isDragged: boolean;
  isInDraggedSubtree: boolean;
  isDraggingInProgress: boolean;
  isCollapsed: boolean;
  className?: string;
  secondaryTotal?: number;
  isTradesView?: boolean;
  updatedFund?: Fund | undefined;
  toggleIsCollapsed: (isCollapsed: boolean) => void;
  onClickAddFund: () => void;
  onUpdateStrategyName: (newName: string) => void;
  onDelete: () => void;
  onSelectStrategy?: () => void;
  onUpdateAllocation: (newAllocation: number) => void;
  onSetDesignation: (designation: PortfolioNodeDesignationEnum) => void;
  onUpdateLots: (lots: Lot[]) => void;
  isLotsModified: boolean;
  onAddStrategy: () => void;
  onDrag: (event: React.MouseEvent<HTMLElement>) => void;
  onToggleDropIndicatorForRow: (showDropIndicator: boolean) => void;
  theme: Theme;
  hasAccessToCompare?: boolean;
  onUpdateChildrenAllocations: (updatedStrategy: Portfolio) => void;
  hideComparisonColumn: boolean;
  rootName: string;
  dataTestId?: string;
  shouldUseNetAssetsInsteadOfAllocation: boolean;
}

interface AllocationPanelRowState {
  isEditingName: boolean;
}

class AllocationPanelRow extends React.Component<AllocationPanelRowProps, AllocationPanelRowState> {
  state = {
    isEditingName: false,
  };

  constructor(props: AllocationPanelRowProps) {
    super(props);
    this.debounceOnClickAddFund = debounce(this.onClickAddFund, 200);
  }

  debounceOnClickAddFund: () => void = () => null;

  componentDidMount() {
    if (!this.props.strategy) {
      return;
    }
    if (this.props.strategy.name === '') {
      this.setState({
        isEditingName: true,
      });
    }
  }

  componentDidUpdate(prevProps: AllocationPanelRowProps) {
    if (this.props.strategy?.name === '' && prevProps.strategy?.id !== this.props.strategy?.id) {
      this.setState({ isEditingName: true });
    }
  }

  getAllocation = (portfolio?: Portfolio) => {
    if (this.props.shouldUseNetAssetsInsteadOfAllocation) {
      return portfolio?.netAssets;
    }
    return portfolio?.allocation;
  };

  onClickAddFund = () => {
    this.props.toggleIsCollapsed(false);
    this.props.onClickAddFund();
  };

  startEditingName = () => {
    this.setState({
      isEditingName: true,
    });
  };

  onSubmitEditingName = (value: string) => {
    this.setState({
      isEditingName: false,
    });
    const { strategy } = this.props;
    if (!value || value === strategy?.name) {
      return;
    }
    this.props.onUpdateStrategyName(value);
  };

  onCompare = () => {
    analyticsService.navigationTriggered({
      destinationPageTitle: 'Comparison',
      itemType: 'dropdown',
      location: 'Strategy actions - Allocation Panel',
      userIntent: 'navigate',
    });
  };

  handleMouseEnter = (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const {
      isGhost,
      isSelected,
      isStrategy,
      isCollapsed,
      isInSelectedSubtree,
      isDragged,
      isInDraggedSubtree,
      isDraggingInProgress,
    } = this.props;

    if (isDraggingInProgress && !isDragged && !isInDraggedSubtree && !isGhost && (isSelected || isInSelectedSubtree)) {
      if (isStrategy && isCollapsed) {
        // Uncollapse strategy after .5 seconds and only then show the drop indicator
        setTimeout(() => {
          if (
            this.props.isDraggingInProgress &&
            !this.props.isDragged &&
            !this.props.isInDraggedSubtree &&
            !this.props.isGhost &&
            (this.props.isSelected || this.props.isInSelectedSubtree)
          ) {
            this.props.toggleIsCollapsed(false);
            this.props.onToggleDropIndicatorForRow(true);
          }
        }, 500);
      } else {
        // If it's not a collapsed strategy, show dropindicator immediately
        this.props.onToggleDropIndicatorForRow(true);
      }
    }
  };

  handleMouseLeave = (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.FocusEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    this.props.onToggleDropIndicatorForRow(false);
  };

  render() {
    const {
      strategy: incompleteStrategy,
      compareStrategy: incompleteCompareStrategy,
      originalNode: incompleteOriginalNode,
      isRoot,
      isGhost,
      isStrategy,
      isSelected,
      totalWidth,
      isInSelectedSubtree,
      hideCompareValue,
      compareLoading,
      isDragged,
      isInDraggedSubtree,
      isDraggingInProgress,
      isCollapsed,
      className,
      isPercentageMode,
      toggleIsCollapsed,
      onDelete,
      onSelectStrategy,
      onUpdateAllocation,
      onSetDesignation,
      onUpdateLots,
      isLotsModified,
      onAddStrategy,
      onDrag,
      baseAllocation,
      orignalBaseAllocation,
      secondaryTotal,
      isTradesView,
      updatedFund,
      theme,
      onUpdateChildrenAllocations,
      hideComparisonColumn,
      rootName,
      dataTestId,
      shouldUseNetAssetsInsteadOfAllocation,
    } = this.props;
    const { isEditingName } = this.state;

    // Fund information might have been updated globally. Update this node with the data that's now auto-saved,
    // so it should apply to all current strategy, original strategy and compare strategy.
    const strategy = getWithUpdatedFund(incompleteStrategy, updatedFund);
    const compareStrategy = getWithUpdatedFund(incompleteCompareStrategy, updatedFund);
    const originalNode = getWithUpdatedFund(incompleteOriginalNode, updatedFund);

    const hasAddFund = FS.has('rcm_portfolio_strategy_enforcement_ff') ? isRoot : isStrategy && !isGhost;
    const name = !isGhost ? strategy!.name : compareStrategy!.name;

    const originalName = originalNode ? originalNode.name : undefined;
    const originalAllocation = originalNode ? this.getAllocation(originalNode) : undefined;

    const isAllocationModified =
      !isGhost && (!originalNode || Math.abs((originalAllocation ?? 0) - (this.getAllocation(strategy) ?? 0)) > eps);
    const isModified = isAllocationModified || isLotsModified;

    const isOutsideOfSelectedSubtree = !isSelected && !isInSelectedSubtree;
    const allowActions = !isGhost && !isOutsideOfSelectedSubtree && !isDraggingInProgress;
    const allowHighlight = !(isGhost && isStrategy) && !isDraggingInProgress;
    const childFundIdsForComparison = compact(
      flattenNode(strategy)
        .filter((fund) => !isOptionNode(fund))
        .map(({ fund }) => fund?.id),
    );
    const isEmptyStrategy = isStrategy && childFundIdsForComparison.length === 0;

    const showIsBeingDraggedHighlight = isDraggingInProgress && (isDragged || isInDraggedSubtree);
    const { Colors } = theme;
    const draggedHighlightColor = !showIsBeingDraggedHighlight
      ? 'none'
      : ColorUtils.hex2rgba(Colors.Primary.Main, isDragged ? 0.6 : 0.2);

    const [fundStart, fundEnd] =
      isStrategy || isGhost
        ? [undefined, undefined]
        : [strategy?.fund?.startRange || strategy?.periodStart, strategy?.fund?.endRange || strategy?.periodEnd];
    const investmentTimeRange =
      !fundStart || !fundEnd
        ? ''
        : `${Dates.toDDMMMYYYY(fundStart, strategy?.fund?.returnFrequency)} - ${Dates.toDDMMMYYYY(
            fundEnd,
            strategy?.fund?.returnFrequency,
          )}`;
    const investmentSymbol = !isGhost ? strategy!.fund?.symbol : compareStrategy!.fund?.symbol;

    const isMissingReturnsMessage =
      !isStrategy && !isGhost && strategy && (strategy.allocation ?? 0) !== 0
        ? getMissingReturnsMessage(strategy, Colors.HighlightLight)
        : undefined;

    const isIncompleteTaxInfo = !isStrategy && !isGhost && !!strategy && hasNodeIncompleteTaxInfo(strategy);

    return (
      <AllocationPanelContext.Consumer>
        {(context) => {
          const { allocationPanelType } = context;
          const isAllocationsForwardFilled =
            context.allocationPanelType === 'historical read-only' ? context.isAllocationsForwardFilled : false;

          const historicalPortfolioNodeAllocationType =
            allocationPanelType === 'historical read-only' &&
            context.allocationsAsOfDateTimestamp &&
            strategy?.fund?.returnFrequency &&
            !isStrategy &&
            strategy?.closingAllocationsTs
              ? getHistoricalPortfolioNodeAllocationType(
                  context.allocationsAsOfDateTimestamp,
                  strategy.closingAllocationsTs.map(([timestamp, _value]) => assertNotNil(timestamp)),
                  strategy.fund.returnFrequency,
                )
              : undefined;

          return (
            <PrintContainerDimensions>
              {({ width }) => (
                <Row
                  role="row"
                  aria-label={`${originalName} allocation row`}
                  className={className ? `${className} qa-allocation-row` : 'qa-allocation-row'}
                  hasAlert={!!isMissingReturnsMessage}
                  hasIncompleteTaxInfo={isIncompleteTaxInfo}
                  isDraggingInProgress={isDraggingInProgress}
                  onMouseOver={this.handleMouseEnter}
                  onMouseOut={this.handleMouseLeave}
                  // For a11y
                  onFocus={this.handleMouseEnter}
                  onBlur={this.handleMouseLeave}
                  data-testid={dataTestId}
                >
                  {allowHighlight && (
                    <RowHighlight
                      totalWidth={totalWidth}
                      className="row-highlight"
                      hasAlert={!!isMissingReturnsMessage}
                      hasWarning={isIncompleteTaxInfo}
                    >
                      <AllocationPanelHandle {...{ allowActions, isRoot, isSelected, allocationPanelType, onDrag }} />
                    </RowHighlight>
                  )}

                  {showIsBeingDraggedHighlight && <DragOverlay totalWidth={totalWidth} color={draggedHighlightColor} />}

                  <ItemName
                    originalName={originalName}
                    name={name}
                    width={width}
                    isGhost={isGhost}
                    allocationItemConfig={(() => {
                      if (strategy?.historical) {
                        if (strategy._tag === 'strategy') {
                          return {
                            allocationPanelType: 'historical read-only',
                            _tag: 'strategy',
                          };
                        }
                        return {
                          allocationPanelType: 'historical read-only',
                          _tag: 'fund',
                          rangeAnalysis: strategy.rangeAnalysis,
                        };
                      }
                      return {
                        allocationPanelType: 'full',
                        _tag: isStrategy ? 'strategy' : 'fund',
                      };
                    })()}
                    isEditing={isEditingName}
                    hasAddFund={hasAddFund && allocationPanelType !== 'historical read-only'}
                    isSelected={isSelected}
                    isCollapsed={isCollapsed}
                    investmentTimeRange={investmentTimeRange}
                    isMissingReturnsMessage={isMissingReturnsMessage}
                    isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
                    onSelectStrategyClick={onSelectStrategy}
                    onAddFundClick={this.debounceOnClickAddFund}
                    onTriangleClick={() => toggleIsCollapsed(!isCollapsed)}
                    onEditNameClick={this.onSubmitEditingName}
                    investmentSymbol={investmentSymbol}
                    hideComparisonColumn={hideComparisonColumn}
                    isAllocationsForwardFilled={isAllocationsForwardFilled && isRoot}
                    historicalPortfolioNodeAllocationType={historicalPortfolioNodeAllocationType}
                    isRoot={isRoot}
                    designation={(strategy ?? compareStrategy)?.designation ?? 'DEFAULT'}
                    isNegativeAssets={
                      shouldUseNetAssetsInsteadOfAllocation && ((strategy ?? compareStrategy)?.netAssets ?? 0) < 0
                    }
                    optionData={(strategy ?? compareStrategy)?.fund?.optionData}
                    frequency={(strategy ?? compareStrategy)?.fund?.returnFrequency}
                    assetType={(strategy ?? compareStrategy)?.fund?.assetType}
                    isIncompleteTaxInfo={isIncompleteTaxInfo}
                  />

                  <ItemAllocationPrimitives.Provider
                    isGhost={isGhost}
                    isStrategy={isStrategy}
                    isRoot={isRoot}
                    value={strategy ? this.getAllocation(strategy) : undefined}
                    originalValue={originalAllocation}
                    onUpdateAllocation={onUpdateAllocation}
                    isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
                    isPercentageMode={isPercentageMode}
                    baseAllocation={baseAllocation}
                    orignalBaseAllocation={orignalBaseAllocation}
                  >
                    <ItemAllocationPrimitives.Root
                      isGhost={isGhost}
                      isStrategy={isStrategy}
                      isRoot={isRoot}
                      isModified={isModified}
                      value={strategy ? this.getAllocation(strategy) : undefined}
                      isOutsideOfSelectedSubtree={isOutsideOfSelectedSubtree}
                      itemType={
                        historicalPortfolioNodeAllocationType
                          ? ItemAllocationType.PublicHistorical({
                              allocationType: historicalPortfolioNodeAllocationType,
                            })
                          : ItemAllocationType.Unknown()
                      }
                    >
                      <ItemAllocationPrimitives.Input
                        isGhost={isGhost}
                        isStrategy={isStrategy}
                        isModified={isModified}
                        isPrivate={false}
                      />
                    </ItemAllocationPrimitives.Root>
                  </ItemAllocationPrimitives.Provider>

                  <ItemActions>
                    {allowActions &&
                      allocationPanelType !== 'historical read-only' &&
                      (isStrategy ? (
                        <AllocationStrategyActions
                          isRoot={isRoot}
                          onAddStrategy={onAddStrategy}
                          onAddInvestment={this.debounceOnClickAddFund}
                          onEditName={this.startEditingName}
                          onDelete={onDelete}
                          onCompare={!this.props.hasAccessToCompare || isEmptyStrategy ? undefined : this.onCompare}
                          compareLink={`${Routes.ANALYSIS_COMPARE_PATH}?${queryString.stringify({ subjects: childFundIdsForComparison })}`}
                          strategy={strategy!}
                          onUpdateChildrenAllocations={onUpdateChildrenAllocations}
                          isPercentageMode={isPercentageMode}
                          rootName={rootName}
                        />
                      ) : (
                        <FundActions
                          node={assertNotNil(strategy)}
                          originalFund={originalNode?.fund}
                          onDelete={onDelete}
                          onSetDesignation={onSetDesignation}
                          onUpdateLots={onUpdateLots}
                        />
                      ))}
                  </ItemActions>

                  {!hideComparisonColumn && (
                    <ItemCompareAllocation
                      isStrategy={isStrategy}
                      isRoot={isRoot}
                      isGreyedOut={isOutsideOfSelectedSubtree}
                    >
                      {!hideCompareValue &&
                        (compareLoading ? (
                          <CellLoader className="qa-loader" />
                        ) : !isTradesView ? (
                          <CompareAllocationCell
                            compareStrategy={compareStrategy}
                            isRoot={isRoot}
                            isPercentageMode={isPercentageMode}
                            secondaryTotal={secondaryTotal}
                          />
                        ) : (
                          <CompareTradeCell
                            strategy={strategy}
                            compareStrategy={compareStrategy}
                            isRoot={isRoot}
                            {...(isPercentageMode
                              ? {
                                  isPercentageMode,
                                  total: assertNotNil(baseAllocation),
                                  secondaryTotal: assertNotNil(secondaryTotal),
                                }
                              : {
                                  isPercentageMode: false,
                                })}
                          />
                        ))}
                    </ItemCompareAllocation>
                  )}
                </Row>
              )}
            </PrintContainerDimensions>
          );
        }}
      </AllocationPanelContext.Consumer>
    );
  }
}

const getWithUpdatedFund = <T extends Portfolio>(strategy?: T, fund?: Fund): T | undefined =>
  !strategy || !fund
    ? strategy
    : {
        ...strategy,
        fund,
        name: strategy.designation === 'CASH' ? strategy.name : fund.name,
      };

export const CompareAllocationCell = ({
  compareStrategy,
  isRoot,
  isPercentageMode,
  secondaryTotal,
}: {
  compareStrategy: Portfolio | undefined;
  isRoot: boolean;
  isPercentageMode: boolean;
  secondaryTotal: number | undefined;
}) => {
  const shouldUseNetAssetsInsteadOfAllocation = useShouldUseNetAssetsInsteadOfAllocation();

  const allocation = shouldUseNetAssetsInsteadOfAllocation ? compareStrategy?.netAssets : compareStrategy?.allocation;

  if (!compareStrategy || isNil(allocation)) {
    return <>--</>;
  }
  return (
    <Tooltip
      usePortal
      content={
        <AllocationValue
          testId="qa-value-compare"
          isPercentageMode={isPercentageMode}
          value={isPercentageMode ? (allocation * 100) / Math.abs(secondaryTotal ?? 1) : allocation}
        />
      }
    >
      {formatAllocation(allocation, isRoot, isPercentageMode, secondaryTotal)}
    </Tooltip>
  );
};

export const CompareTradeCell = ({
  strategy,
  compareStrategy,
  isRoot,
  ...percentageProps
}: {
  strategy: Portfolio | undefined;
  compareStrategy: Portfolio | undefined;
  isRoot: boolean;
} & (
  | {
      isPercentageMode: true;
      total: number;
      secondaryTotal: number;
    }
  | {
      isPercentageMode: false;
      total?: undefined;
      secondaryTotal?: undefined;
    }
)) => {
  const shouldUseNetAssetsInsteadOfAllocation = useShouldUseNetAssetsInsteadOfAllocation();

  const strategyAllocation = (shouldUseNetAssetsInsteadOfAllocation ? strategy?.netAssets : strategy?.allocation) ?? 0;
  const compareAllocation =
    (shouldUseNetAssetsInsteadOfAllocation ? compareStrategy?.netAssets : compareStrategy?.allocation) ?? 0;

  const strategyValue =
    strategyAllocation === 0
      ? 0
      : !percentageProps.isPercentageMode
        ? strategyAllocation
        : (strategyAllocation * 100) / percentageProps.total;
  const compareValue =
    compareAllocation === 0
      ? 0
      : !percentageProps.isPercentageMode
        ? compareAllocation
        : (compareAllocation * 100) / percentageProps.secondaryTotal;

  const value = compareValue - strategyValue;

  if (value.toFixed(1) === '0.0') {
    return <>--</>;
  }

  return (
    <Tooltip
      usePortal
      content={
        <AllocationDifference
          testId="qa-diff-compare"
          difference={value}
          isPercentageMode={percentageProps.isPercentageMode}
        />
      }
    >
      {formatAllocation(
        value,
        isRoot,
        false, // isPercentage: false bc we are displaying the relative difference between the % values
        undefined, // total: unnecessary when isPercentage is false
        true, // isTrade
      )}
    </Tooltip>
  );
};

export default withRouter(withTheme(AllocationPanelRow));

const FullWidthSpace = styled.div<{ totalWidth: number; className?: string }>`
  position: absolute;
  right: 0;
  width: ${({ totalWidth }) => totalWidth}px;
  height: 30px;
`;

const RowHighlight = styled(FullWidthSpace)<{ hasAlert: boolean; hasWarning: boolean }>`
  /* The transparent background is there to allow for the hover state to be triggered (and the highlight background
   * color changed to grey) at any place in the row.
   */

  background-color: ${({ hasAlert, hasWarning }) =>
    hasAlert
      ? ColorUtils.hex2rgbaFrom(GetColor.Alert, 0.2)
      : hasWarning
        ? GetColor.DataLibraryColor.Warning
        : 'rgba(255, 255, 255, 0)'};

  & > .drag-and-drop-handle {
    display: none;
  }

  top: 0;
`;

const DragOverlay = styled(FullWidthSpace)<{ color: string }>`
  background-color: ${({ color }) => color};
  z-index: ${ZIndex.Front};
  top: 0;
`;

export const Row = styled.div<{ isDraggingInProgress: boolean; hasAlert: boolean; hasIncompleteTaxInfo: boolean }>`
  display: flex;
  align-items: center;
  height: 30px;
  padding-left: ${Constants.NAME_MARGIN}px;

  ${({ isDraggingInProgress, hasAlert, hasIncompleteTaxInfo }) =>
    !isDraggingInProgress
      ? css`
          &:hover {
            & > .row-highlight {
              background-color: ${hasAlert
                ? GetColor.Alert
                : hasIncompleteTaxInfo
                  ? GetColor.DataLibraryColor.Warning
                  : ColorUtils.hex2rgbaFrom(GetColor.GreyScale.Grey90, 0.1)};

              & > .drag-and-drop-handle {
                display: inline;
              }
            }

            .${IncreasedSpecificityGhostFundWrapperClass} .${GhostFundAllocationInputClass} {
              display: block !important;
            }

            .${IncreasedSpecificityGhostFundWrapperClass} .${GhostFundAllocationValueClass} {
              display: none !important;
            }

            & > .${GhostFundNameClass}, .${OutOfSubtreeNameClass} {
              color: ${GetColor.Black};
            }

            & .${OPTION_ICON_CLASS} {
              color: ${GetColor.GreyScale.Grey60};
            }

            & .${CASH_ICON_POSITIVE_CLASS} {
              color: ${GetColor.Green};
            }

            & .${CASH_ICON_NEGATIVE_CLASS} {
              color: ${GetColor.Error};
            }

            ${ActionsButton} {
              i {
                opacity: 1;
              }
            }
          }

          ${ActionsButton}:focus {
            i {
              opacity: 1;
            }
          }
        `
      : css`
          & > * {
            pointer-events: none;
          }
        `}
`;

const ItemActions = styled.div`
  width: ${Constants.ACTIONS_WIDTH}px;
  min-width: ${Constants.ACTIONS_WIDTH}px;
  height: 100%;
  z-index: ${ZIndex.Front};
`;

const ItemCompareAllocation = styled.div<{
  isRoot: boolean;
  isStrategy: boolean;
  isGreyedOut: boolean;
}>`
  ${({ isRoot }) => css`
    font-size: ${isRoot ? 16 : 14}px;
  `}
  ${({ isStrategy }) =>
    isStrategy &&
    css`
      font-weight: bold;
    `}
  ${({ isGreyedOut }) =>
    isGreyedOut &&
    css`
      color: ${GetColor.GreyScale.Grey50};
    `}
  width: ${Constants.COMPARE_WIDTH}px;
  min-width: ${Constants.COMPARE_WIDTH}px;
  height: 100%;
  padding-right: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
